import { htmlToPlainPreview } from './htmlToPlainPreview';

export type SupportedMessageLanguage = 'fr' | 'en' | 'de';

const MIN_CHARS = 40;

const STOP_WORDS: Record<SupportedMessageLanguage, string[]> = {
    fr: [
        'le', 'la', 'les', 'un', 'une', 'des', 'de', 'du', 'et', 'est', 'que', 'qui',
        'pour', 'dans', 'pas', 'sur', 'avec', 'nous', 'vous', 'je', 'il', 'elle',
        'bonjour', 'merci', 'cordialement', 'veuillez', 'aussi', 'cette', 'votre',
    ],
    en: [
        'the', 'and', 'for', 'that', 'with', 'you', 'are', 'this', 'have', 'from',
        'not', 'your', 'was', 'but', 'they', 'will', 'can', 'please', 'hello',
        'thanks', 'thank', 'regards', 'would', 'could', 'about', 'into',
    ],
    de: [
        'der', 'die', 'das', 'und', 'ist', 'nicht', 'mit', 'von', 'den', 'dem',
        'ein', 'eine', 'ich', 'sie', 'wir', 'auf', 'für', 'auch', 'oder', 'aber',
        'guten', 'tag', 'bitte', 'danke', 'freundliche', 'grüsse', 'grüße', 'sehr',
        'geehrte', 'haben', 'werden',
    ],
};

export const UI_LANGUAGE_NAMES: Record<SupportedMessageLanguage, string> = {
    fr: 'French',
    en: 'English',
    de: 'German',
};

/**
 * Normalize UI / BCP-47 codes to fr|en|de when supported.
 */
export function normalizeUiLanguage(lang: string | undefined | null): SupportedMessageLanguage | null {
    const code = String(lang || '').split('-')[0].toLowerCase();
    if (code === 'fr' || code === 'en' || code === 'de')
        return code;
    return null;
}

/**
 * Pick the translated string for the UI language only.
 * Never falls back to another locale (avoids showing English when UI is French).
 */
export function pickUiTranslation(
    translations: Record<string, unknown> | null | undefined,
    lang: SupportedMessageLanguage
): string | null {
    if (!translations || typeof translations !== 'object')
        return null;

    const name = UI_LANGUAGE_NAMES[lang];
    const candidates = [
        translations[lang],
        translations[name],
        translations[name.toLowerCase()],
        translations[lang.toUpperCase()],
    ];

    for (const value of candidates) {
        if (typeof value === 'string' && value.trim())
            return value;
    }

    return null;
}

function tokenize(text: string): string[] {
    return text
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s]/gu, ' ')
        .split(/\s+/)
        .filter(Boolean);
}

function scoreLanguage(tokens: string[], lang: SupportedMessageLanguage): number {
    const words = STOP_WORDS[lang];
    let score = 0;
    for (const token of tokens) {
        if (words.includes(token))
            score += 1;
    }
    // German umlaut / ß are strong signals even without stop-word hits.
    if (lang === 'de' && /[äöüß]/i.test(tokens.join(' ')))
        score += 3;
    return score;
}

/**
 * Lightweight fr/en/de detection from HTML or plain text.
 * Returns null when the sample is too short or scores are tied / weak.
 */
export function detectMessageLanguage(htmlOrText: string): SupportedMessageLanguage | null {
    const plain = htmlToPlainPreview(htmlOrText, 0);
    if (!plain || plain.length < MIN_CHARS)
        return null;

    const tokens = tokenize(plain);
    if (tokens.length < 6)
        return null;

    const scores: Record<SupportedMessageLanguage, number> = {
        fr: scoreLanguage(tokens, 'fr'),
        en: scoreLanguage(tokens, 'en'),
        de: scoreLanguage(tokens, 'de'),
    };

    const ranked = (Object.keys(scores) as SupportedMessageLanguage[])
        .map((lang) => ({ lang, score: scores[lang] }))
        .sort((a, b) => b.score - a.score);

    const best = ranked[0];
    const second = ranked[1];
    if (!best || best.score < 2)
        return null;
    if (second && best.score === second.score)
        return null;
    // Require a clear margin when the runner-up also scored.
    if (second && second.score > 0 && best.score - second.score < 2)
        return null;

    return best.lang;
}

/**
 * True when detected language is known and differs from the UI language.
 */
export function isForeignLanguage(
    detected: SupportedMessageLanguage | null,
    uiLang: string | undefined | null
): boolean {
    if (!detected)
        return false;
    const ui = normalizeUiLanguage(uiLang);
    if (!ui)
        return false;
    return detected !== ui;
}
